import { type ReactNode } from 'react';

import {
    Input as AriaInput,
    TextFieldProps as AriaTextFieldProps,
    TextField as AriaTextField,
    ValidationResult,
} from 'react-aria-components';

import { css } from '@allenai/varnish-panda-runtime/css';

import { cx } from '@/utils/cx';

import { Description } from '@/components/field/description/Description';
import { FieldError } from '@/components/field/fieldError/FieldError';
import { FormInputContainer } from '@/components/field/formInputContainer/FormInputContainer';
import { Label } from '@/components/field/label/Label';
import { childFocusRing } from '@/components/shared/focusRing.styles';

import { inputRecipe, InputRecipeProps } from './input.styles';

interface InputProps extends AriaTextFieldProps, InputRecipeProps {
    label?: string;
    errorMessage?: string | ((validation: ValidationResult) => string);
    description?: string;
    // controls
    startControls?: ReactNode;
    endControls?: ReactNode;
    startControlsClassName?: string;
    endControlsClassName?: string;
    // classeNames
    className?: string;
    containerClassName?: string;
    inputClassName?: string;
    labelClassName?: string;
    errorClassName?: string;
    descriptionClassName?: string;
}

const Input = ({
    label,
    errorMessage,
    description,
    // controls
    startControls,
    endControls,
    startControlsClassName,
    endControlsClassName,
    // classes
    className,
    containerClassName,
    inputClassName,
    labelClassName,
    errorClassName,
    descriptionClassName,
    ...rest
}: InputProps) => {
    const [variantProps, localProps] = inputRecipe.splitVariantProps(rest);
    const recipeClassNames = inputRecipe(variantProps);
    const { size } = rest;
    return (
        <AriaTextField className={cx('group', recipeClassNames.root, className)} {...localProps}>
            <Label size={size} className={cx(recipeClassNames.label, labelClassName)}>
                {label}
            </Label>

            <FormInputContainer
                startControls={startControls}
                endControls={endControls}
                startControlsClassName={cx(recipeClassNames.startControls, startControlsClassName)}
                endControlsClassName={cx(recipeClassNames.endControls, endControlsClassName)}
                className={cx(css(childFocusRing), recipeClassNames.container, containerClassName)}
                {...variantProps}>
                <AriaInput className={cx(recipeClassNames.input, inputClassName)} />
            </FormInputContainer>

            <Description
                size={size}
                className={cx(recipeClassNames.description, descriptionClassName)}>
                {description}
            </Description>
            <FieldError size={size} className={cx(recipeClassNames.error, errorClassName)}>
                {errorMessage}
            </FieldError>
        </AriaTextField>
    );
};

export { Input };
export type { InputProps };
